import { useEffect, useMemo, useRef, useState } from 'react'
import { browserHistory } from 'react-router'
import dayjs from 'dayjs'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { getMyClients } from 'enrollment-platform/api/persons'

import {
  ListTransactionsParams,
  TransactionsSummaryParams,
  useTransactionsQuery,
  useTransactionsSummaryQuery,
  useTransactionYearsQuery,
} from '../../../api/transaction-details-api-client'
import {
  ALL_NOTE_TYPES,
  ALL_PAYMENT_SOURCES,
  ALL_RECON_STATUSES,
  buildDisplayRows,
  isSortColumnKey,
  RECON_STATUS_OPTIONS,
  SortColumnKey,
  SortOrder,
} from '../helpers'
import { isTestCompany } from '../../ReconciliationV2/BenefitsTable/helpers'
import { replaceOwnedQuery } from '../../ReconciliationV2/replaceOwnedQuery'

/**
 * URL-synced filter/pagination state for the Transaction Details panel —
 * follows the shape of BenefitsTable's `readInitialFilters` + replace-on-change
 * pattern (see BenefitsTable/index.tsx + helpers.tsx) rather than
 * usePaymentAssignment's fuller apply/pending-filter machinery, since this
 * page's filters all apply live (there's no separate "Apply" step).
 *
 * Query keys are `td*`-prefixed so they can coexist on Payment Reconciliation
 * with BenefitsTable's `q`/`page`/`year`/`sortBy`/`sortOrder`. Writes merge
 * via replaceOwnedQuery so visiting this tab does not wipe Benefits filters.
 *
 * Only `page` (not `limit`/pageSize) round-trips through the URL — same as
 * BenefitsTable, whose `limit` is local `useState(25)` with no URL sync.
 * pageSize therefore resets to 25 each session even if the URL still has a
 * `page` beyond that default's range; antd's Pagination clamps it.
 */

export interface MonthOption {
  value: number
  label: string
}

/** A Client filter option: `value` is the client's public key. */
export interface ClientOption {
  value: string
  label: string
}

/** Backend-enforced bounds on the `year` query param. */
export const MIN_YEAR = 2020
export const MAX_YEAR = 2100

interface UrlState {
  search: string
  sources: string[]
  year: number
  month: number | undefined
  client: string | undefined
  reconStatuses: string[]
  splitOnly: boolean
  hasAbsorption: boolean
  noteTypes: string[]
  noteSearch: string
  page: number
  sortBy: SortColumnKey | undefined
  sortOrder: SortOrder | undefined
  /** One-shot deep-link: auto-open the detail modal for this transaction id. */
  openTransactionId: string | undefined
}

/**
 * Prefixed so these never collide with BenefitsTable on the shared route.
 * `q` / `open` also form the deep-link contract used by the Adjust Payment
 * modal to jump into this panel (see AdjustPaymentModal's "open in Transaction
 * Details" action): `q` filters the ledger to a transaction id and `open`
 * auto-opens its detail modal once loaded.
 */
export const TD_Q = {
  q: 'tdQ',
  types: 'tdTypes',
  year: 'tdYear',
  month: 'tdMonth',
  client: 'tdClient',
  recon: 'tdRecon',
  split: 'tdSplit',
  hasAbsorption: 'tdHasAbsorption',
  noteTypes: 'tdNoteTypes',
  noteSearch: 'tdNote',
  page: 'tdPage',
  sortBy: 'tdSortBy',
  sortOrder: 'tdSortOrder',
  open: 'tdOpen',
} as const

const TD_URL_KEYS = Object.values(TD_Q)
/** Filter sync must not clear `tdOpen` — the panel owns clearing that one-shot. */
const TD_FILTER_URL_KEYS = TD_URL_KEYS.filter((key) => key !== TD_Q.open)

/**
 * Keys Adjust Payment replaces when jumping into Transaction Details. Writing
 * only the deep-link fields would leave stale tdTypes/tdMonth/… in the URL
 * (and in React state when the panel is already mounted), which can hide the
 * target transaction from the list/auto-open.
 */
export const TD_DEEP_LINK_URL_KEYS = [
  'tab',
  TD_Q.q,
  TD_Q.open,
  TD_Q.year,
  TD_Q.types,
  TD_Q.month,
  TD_Q.client,
  TD_Q.recon,
  TD_Q.split,
  TD_Q.noteTypes,
  TD_Q.noteSearch,
  TD_Q.page,
  TD_Q.sortBy,
  TD_Q.sortOrder,
] as const

const readInitialState = (
  query: Record<string, string | undefined>
): UrlState => {
  const currentYear = dayjs().year()
  const parsedPage = parseInt(query[TD_Q.page] ?? '', 10)
  const parsedMonth = parseInt(query[TD_Q.month] ?? '', 10)
  const parsedYear = parseInt(query[TD_Q.year] ?? '', 10)
  const typesParam = query[TD_Q.types] ?? ''
  const types = typesParam ? typesParam.split(',').filter(Boolean) : null
  const sources = types
    ? types.filter((t) => ALL_PAYMENT_SOURCES.includes(t))
    : ALL_PAYMENT_SOURCES.slice()
  const reconParam = query[TD_Q.recon] ?? ''
  const reconTypes = reconParam ? reconParam.split(',').filter(Boolean) : []
  const reconStatuses = reconTypes.filter((r) => ALL_RECON_STATUSES.includes(r))
  // Read in option order, which also drops unknown and repeated entries.
  const listedNoteTypes = (query[TD_Q.noteTypes] ?? '').split(',')
  const noteTypes = ALL_NOTE_TYPES.filter((t) => listedNoteTypes.includes(t))
  // sortOrder only counts when paired with a recognized sortBy — a lone
  // sortOrder with no column doesn't mean anything.
  const sortByParam = query[TD_Q.sortBy]
  const sortBy =
    sortByParam && isSortColumnKey(sortByParam) ? sortByParam : undefined
  const sortOrderParam = query[TD_Q.sortOrder]
  const sortOrder: SortOrder | undefined =
    sortBy && sortOrderParam === 'asc'
      ? 'asc'
      : sortBy && sortOrderParam === 'desc'
        ? 'desc'
        : undefined
  const openTransactionId = query[TD_Q.open] || undefined
  const month =
    Number.isFinite(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12
      ? parsedMonth
      : undefined
  const client = query[TD_Q.client] || undefined
  const splitOnly = query[TD_Q.split] === 'true'
  return {
    search: query[TD_Q.q] ?? '',
    // Deep link must not inherit restrictive filters from a prior ledger view —
    // only search + optional year (and the open id) matter for auto-open.
    sources: openTransactionId ? ALL_PAYMENT_SOURCES.slice() : sources,
    year:
      Number.isFinite(parsedYear) &&
      parsedYear >= MIN_YEAR &&
      parsedYear <= MAX_YEAR
        ? parsedYear
        : currentYear,
    month: openTransactionId ? undefined : month,
    client: openTransactionId ? undefined : client,
    reconStatuses: openTransactionId ? [] : reconStatuses,
    splitOnly: openTransactionId ? false : splitOnly,
    hasAbsorption: openTransactionId
      ? false
      : query[TD_Q.hasAbsorption] === 'true',
    noteTypes: openTransactionId ? [] : noteTypes,
    noteSearch: openTransactionId ? '' : (query[TD_Q.noteSearch] ?? ''),
    page: openTransactionId
      ? 1
      : Number.isNaN(parsedPage) || parsedPage < 1
        ? 1
        : parsedPage,
    sortBy: openTransactionId ? undefined : sortBy,
    sortOrder: openTransactionId ? undefined : sortOrder,
    openTransactionId,
  }
}

export const useTransactionDetails = () => {
  // Seed from the URL on mount. Later filter edits update React state
  // directly; deep links from Adjust Payment's "Open in Transaction Details"
  // also arrive via browserHistory while this panel can already be mounted, so
  // a history listener below applies tdQ / tdYear / tdOpen when tdOpen appears.
  const [initial] = useState(() => {
    const query = (browserHistory.getCurrentLocation().query || {}) as Record<
      string,
      string | undefined
    >
    return readInitialState(query)
  })
  // Deep link already consumed by this hook, so the listener can tell an
  // inbound jump apart from a history event that merely still carries the same
  // tdOpen. Seeded from mount because the initial state above consumed it.
  const appliedDeepLinkRef = useRef<string | undefined>(
    initial.openTransactionId
  )
  // Stable for the component's lifetime — used as the "default" year both to
  // decide what to omit from the URL and what `reset()` restores.
  const [currentYear] = useState(() => dayjs().year())

  const [search, setSearch] = useState(initial.search)
  const [appliedSearch, setAppliedSearch] = useState(initial.search)
  const [selectedSources, setSelectedSources] = useState<string[]>(
    initial.sources
  )
  const [year, setYearState] = useState(initial.year)
  const [monthFilter, setMonthFilterState] = useState<number | undefined>(
    initial.month
  )
  const [clientFilter, setClientFilterState] = useState<string | undefined>(
    initial.client
  )
  const [reconStatuses, setReconStatusesState] = useState<string[]>(
    initial.reconStatuses
  )
  const [splitOnly, setSplitOnlyState] = useState(initial.splitOnly)
  const [hasAbsorption, setHasAbsorptionState] = useState(initial.hasAbsorption)
  const [noteTypes, setNoteTypesState] = useState<string[]>(initial.noteTypes)
  // Draft vs applied, like `search` / `appliedSearch`: the text box applies on
  // Enter (or when emptied), not on every keystroke.
  const [noteSearch, setNoteSearch] = useState(initial.noteSearch)
  const [appliedNoteSearch, setAppliedNoteSearch] = useState(initial.noteSearch)
  const [page, setPage] = useState(initial.page)
  // Not URL-synced (see file header) — resets to the default each session,
  // same as BenefitsTable's local `useState(25)`.
  const [limit, setLimit] = useState(25)
  const [sortBy, setSortByState] = useState<SortColumnKey | undefined>(
    initial.sortBy
  )
  const [sortOrder, setSortOrderState] = useState<SortOrder | undefined>(
    initial.sortOrder
  )
  // One-shot auto-open target. `deepLinkGen` bumps on every inbound tdOpen
  // (including the same id again) so the panel effect re-runs while mounted.
  const [openTransactionId, setOpenTransactionId] = useState(
    initial.openTransactionId
  )
  const [deepLinkGen, setDeepLinkGen] = useState(0)

  // Apply Adjust Payment → Transaction Details deep links while this hook is
  // already mounted (tab already open). Only reacts when tdOpen is present so
  // ordinary filter URL sync and clearing tdOpen do not overwrite local state.
  // Clears every other ledger filter (and resets year when tdYear is absent)
  // so an existing payment-type / month / client / recon / split / year
  // selection cannot hide the target transaction.
  useEffect(() => {
    const unlisten = browserHistory.listen((location) => {
      const query = (location.query || {}) as Record<string, string | undefined>
      const openId = query[TD_Q.open] || undefined
      // tdOpen gone — the panel consumed it (or a plain navigation dropped it),
      // so the next inbound link counts as new even for the same transaction.
      if (!openId) {
        appliedDeepLinkRef.current = undefined
        return
      }
      // The filter sync below writes tdOpen-preserving URLs (it only owns
      // TD_FILTER_URL_KEYS), so every apply here re-emits a history event that
      // still carries this id. Re-applying it would hand the sync new array
      // instances for sources/recon statuses and spin forever — that's most
      // visible while the panel is still fetching and has yet to clear tdOpen.
      if (openId === appliedDeepLinkRef.current) return
      appliedDeepLinkRef.current = openId

      const nextSearch = query[TD_Q.q] ?? ''
      setOpenTransactionId(openId)
      setDeepLinkGen((g) => g + 1)
      setSearch(nextSearch)
      setAppliedSearch(nextSearch)
      setSelectedSources(ALL_PAYMENT_SOURCES.slice())
      setMonthFilterState(undefined)
      setClientFilterState(undefined)
      setReconStatusesState([])
      setSplitOnlyState(false)
      setNoteTypesState([])
      setNoteSearch('')
      setAppliedNoteSearch('')
      setSortByState(undefined)
      setSortOrderState(undefined)
      setPage(1)
      // Same year rule as readInitialState: a valid tdYear wins, otherwise
      // the current calendar year. Skipping setYear when tdYear is omitted
      // (Adjust Payment does that when transactionDate is null) would leave
      // a prior-year filter in place, hide a different-year row, and make
      // auto-open give up after clearing tdOpen.
      const parsedYear = parseInt(query[TD_Q.year] ?? '', 10)
      setYearState(
        Number.isFinite(parsedYear) &&
          parsedYear >= MIN_YEAR &&
          parsedYear <= MAX_YEAR
          ? parsedYear
          : currentYear
      )
    })
    return unlisten
  }, [currentYear])

  // Same Company filter as the Benefits tab (BenefitsTable): the
  // administered-customers list minus test/mock companies, sent as `clientId`
  // and matched on the benefit's ClientPublicKey.
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  useEffect(() => {
    getMyClients()
      .then((customers: SimpleCustomerDto[]) =>
        setClients(customers.filter((c) => !isTestCompany(c)))
      )
      .catch(() => {
        /* non-fatal */
      })
  }, [])

  const allSourcesSelected =
    selectedSources.length === ALL_PAYMENT_SOURCES.length
  const noSourcesSelected = selectedSources.length === 0
  const sourcesParam = allSourcesSelected
    ? undefined
    : selectedSources.join(',')

  // Both "none selected" and "all selected" mean "no filter" per the backend
  // contract — unlike sources, an empty reconStatuses selection does NOT
  // disable the query (there's no "noStatusesSelected" empty state here).
  const reconStatusParam =
    reconStatuses.length === 0 ||
    reconStatuses.length === RECON_STATUS_OPTIONS.length
      ? undefined
      : reconStatuses.join(',')

  // Any selection filters, including all three — see NOTE_TYPE_OPTIONS.
  const noteTypesParam = noteTypes.length > 0 ? noteTypes.join(',') : undefined
  const noteSearchParam = appliedNoteSearch.trim() || undefined

  const listParams: ListTransactionsParams = useMemo(
    () => ({
      search: appliedSearch || undefined,
      sources: sourcesParam,
      year,
      coverageMonth: monthFilter,
      clientId: clientFilter,
      reconStatus: reconStatusParam,
      splitOnly: splitOnly || undefined,
      hasAbsorption: hasAbsorption || undefined,
      noteTypes: noteTypesParam,
      noteSearch: noteSearchParam,
      page,
      limit,
      sortBy,
      sortOrder,
    }),
    [
      appliedSearch,
      sourcesParam,
      year,
      monthFilter,
      clientFilter,
      reconStatusParam,
      splitOnly,
      hasAbsorption,
      noteTypesParam,
      noteSearchParam,
      page,
      limit,
      sortBy,
      sortOrder,
    ]
  )

  const summaryParams: TransactionsSummaryParams = useMemo(
    () => ({
      search: appliedSearch || undefined,
      sources: sourcesParam,
      year,
      coverageMonth: monthFilter,
      clientId: clientFilter,
      reconStatus: reconStatusParam,
      splitOnly: splitOnly || undefined,
      hasAbsorption: hasAbsorption || undefined,
      noteTypes: noteTypesParam,
      noteSearch: noteSearchParam,
    }),
    [
      appliedSearch,
      sourcesParam,
      year,
      monthFilter,
      clientFilter,
      reconStatusParam,
      splitOnly,
      hasAbsorption,
      noteTypesParam,
      noteSearchParam,
    ]
  )

  const { data, isLoading, isFetching, refetch } = useTransactionsQuery(
    listParams,
    { enabled: !noSourcesSelected }
  )
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
  } = useTransactionsSummaryQuery(summaryParams, {
    enabled: !noSourcesSelected,
  })

  // Static — all 12 calendar months are always selectable, independent of
  // what's been loaded. `coverageMonth` mapping is unchanged; only the option
  // list this used to derive from `seenMonths` (loaded-data months) changes.
  const monthOptions: MonthOption[] = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: dayjs().month(i).format('MMMM'),
      })),
    []
  )
  const clientOptions: ClientOption[] = useMemo(
    () => clients.map(({ id, name }) => ({ value: id, label: name })),
    [clients]
  )

  // When every payment type is deselected the queries are disabled, but
  // keepPreviousData still holds the last successful result — blank the
  // derived surfaces so stats, pagination, and Export don't show/download
  // the prior filter's transactions.
  const displayRows = useMemo(
    () => (noSourcesSelected ? [] : buildDisplayRows(data?.data ?? [])),
    [data, noSourcesSelected]
  )
  const total = noSourcesSelected ? 0 : (data?.total ?? 0)
  const effectiveSummary = noSourcesSelected ? undefined : summary

  // Reflect filters/pagination in the URL (shareable view), replacing rather
  // than pushing so filter tweaks don't spam browser history. Skips the very
  // first run so a shared link isn't immediately rewritten on mount.
  const isFirstUrlSync = useRef(true)
  useEffect(() => {
    if (isFirstUrlSync.current) {
      isFirstUrlSync.current = false
      return
    }
    const query: Record<string, string> = {}
    if (appliedSearch) query[TD_Q.q] = appliedSearch
    if (!allSourcesSelected) query[TD_Q.types] = selectedSources.join(',')
    if (year !== currentYear) query[TD_Q.year] = String(year)
    if (monthFilter) query[TD_Q.month] = String(monthFilter)
    if (clientFilter) query[TD_Q.client] = clientFilter
    // Only a partial selection is meaningful in the URL — 0 or "all" both
    // mean "no filter" (see reconStatusParam above).
    if (
      reconStatuses.length > 0 &&
      reconStatuses.length < RECON_STATUS_OPTIONS.length
    ) {
      query[TD_Q.recon] = reconStatuses.join(',')
    }
    if (splitOnly) query[TD_Q.split] = 'true'
    if (hasAbsorption) query[TD_Q.hasAbsorption] = 'true'
    if (noteTypesParam) query[TD_Q.noteTypes] = noteTypesParam
    if (noteSearchParam) query[TD_Q.noteSearch] = noteSearchParam
    if (page > 1) query[TD_Q.page] = String(page)
    if (sortBy) {
      query[TD_Q.sortBy] = sortBy
      if (sortOrder) query[TD_Q.sortOrder] = sortOrder
    }
    replaceOwnedQuery(TD_FILTER_URL_KEYS, query)
  }, [
    appliedSearch,
    allSourcesSelected,
    selectedSources,
    year,
    currentYear,
    monthFilter,
    clientFilter,
    reconStatuses,
    splitOnly,
    hasAbsorption,
    noteTypesParam,
    noteSearchParam,
    page,
    sortBy,
    sortOrder,
  ])

  const applySearch = (value: string) => {
    setAppliedSearch(value)
    setPage(1)
  }

  const setSelectedSourcesAndReset = (sources: string[]) => {
    setSelectedSources(sources)
    setPage(1)
  }

  const setYear = (value: number) => {
    setYearState(value)
    setPage(1)
  }

  const setMonthFilter = (value: number | undefined) => {
    setMonthFilterState(value)
    setPage(1)
  }

  const setClientFilter = (value: string | undefined) => {
    setClientFilterState(value)
    setPage(1)
  }

  const setReconStatuses = (statuses: string[]) => {
    setReconStatusesState(statuses)
    setPage(1)
  }

  const setSplitOnly = (value: boolean) => {
    setSplitOnlyState(value)
    setPage(1)
  }

  const setHasAbsorption = (value: boolean) => {
    setHasAbsorptionState(value)
    setPage(1)
  }

  const setNoteTypes = (types: string[]) => {
    setNoteTypesState(types)
    setPage(1)
  }

  const applyNoteSearch = (value: string) => {
    setAppliedNoteSearch(value)
    setPage(1)
  }

  // 3-state cycle per header click: asc → desc → cleared (back to the
  // backend's default date-desc ordering), mirroring BenefitsTable's
  // cycleSort — except the third click here clears sortBy entirely instead
  // of falling back to another specific column.
  const toggleSort = (key: SortColumnKey) => {
    if (sortBy !== key) {
      setSortByState(key)
      setSortOrderState('asc')
    } else if (sortOrder === 'asc') {
      setSortOrderState('desc')
    } else {
      setSortByState(undefined)
      setSortOrderState(undefined)
    }
    setPage(1)
  }

  const reset = () => {
    setSearch('')
    setAppliedSearch('')
    setSelectedSources(ALL_PAYMENT_SOURCES.slice())
    setYearState(currentYear)
    setMonthFilterState(undefined)
    setClientFilterState(undefined)
    setReconStatusesState([])
    setSplitOnlyState(false)
    setHasAbsorptionState(false)
    setNoteTypesState([])
    setNoteSearch('')
    setAppliedNoteSearch('')
    setSortByState(undefined)
    setSortOrderState(undefined)
    setPage(1)
  }

  // Year options are the calendar years that actually have transaction data
  // (newest first) — so a year with no rows (e.g. 2023) isn't offered. The
  // currently selected `year` is always kept in the list so the Select stays
  // valid before the years load and when the default (current year) has no
  // data. Default selection is unchanged (current calendar year).
  const { data: yearsData } = useTransactionYearsQuery()
  const yearOptions: number[] = useMemo(() => {
    const years = new Set<number>(yearsData?.years ?? [])
    years.add(year)
    return Array.from(years).sort((a, b) => b - a)
  }, [yearsData, year])

  return {
    // search
    search,
    setSearch,
    appliedSearch,
    applySearch,
    // filters
    selectedSources,
    setSelectedSources: setSelectedSourcesAndReset,
    allSourcesSelected,
    noSourcesSelected,
    year,
    setYear,
    yearOptions,
    monthFilter,
    setMonthFilter,
    monthOptions,
    clientFilter,
    setClientFilter,
    clientOptions,
    reconStatuses,
    setReconStatuses,
    splitOnly,
    setSplitOnly,
    hasAbsorption,
    setHasAbsorption,
    noteTypes,
    setNoteTypes,
    noteSearch,
    setNoteSearch,
    appliedNoteSearch,
    applyNoteSearch,
    reset,
    // sorting
    sortBy,
    sortOrder,
    toggleSort,
    // pagination
    page,
    setPage,
    limit,
    setLimit,
    total,
    // deep link — auto-open the detail modal for this transaction id
    openTransactionId,
    deepLinkGen,
    // data
    data,
    displayRows,
    isLoading,
    isFetching,
    refetch,
    summary: effectiveSummary,
    isSummaryLoading,
    isSummaryFetching,
  }
}
